import { useState, useEffect } from 'react';
import { MonthlyStats } from '../../types/property';
import { Card } from '../ui/Card';
import { Select } from '../ui/Select';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell
} from 'recharts';

interface YearlyStatsProps {
  monthlyStats: MonthlyStats[];
}

const MONTHS = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const COLORS = ['#ef4444', '#22c55e', '#3b82f6'];

export const YearlyStats = ({ monthlyStats }: YearlyStatsProps) => {
  const availableYears = [...new Set(monthlyStats.map(stat => stat.year))].sort((a, b) => b - a);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);

  useEffect(() => {
    if (availableYears.length > 0 && !selectedYear) {
      setSelectedYear(availableYears[0]);
    }
  }, [availableYears]);

  if (!selectedYear) return null;

  const yearlyStats = monthlyStats.filter(stat => stat.year === selectedYear);

  const calculateYearTotal = () => {
    return yearlyStats.reduce((acc, stat) => ({
      revenue: acc.revenue + stat.revenue,
      cleaningCosts: acc.cleaningCosts + stat.cleaningCosts,
      inmohostCosts: acc.inmohostCosts + stat.inmohostCosts,
      profit: acc.profit + stat.profit,
      occupancyRate: acc.occupancyRate + stat.occupancyRate,
      reservations: acc.reservations + stat.reservations,
      averageNightPrice: acc.averageNightPrice + stat.averageNightPrice,
      averageStayDuration: acc.averageStayDuration + stat.averageStayDuration
    }), {
      revenue: 0,
      cleaningCosts: 0,
      inmohostCosts: 0,
      profit: 0,
      occupancyRate: 0,
      reservations: 0,
      averageNightPrice: 0,
      averageStayDuration: 0
    });
  };

  const yearTotal = calculateYearTotal();
  const monthCount = yearlyStats.length;

  const summaryCards = [
    {
      title: 'Facturación Total',
      value: `${yearTotal.revenue.toLocaleString()}€`,
      description: 'Ingresos totales generados durante el año, incluyendo todas las reservas realizadas.',
      icon: (
        <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
          <svg className="w-6 h-6 text-red-600" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1.41 16.09V20h-2.67v-1.93c-1.71-.36-3.16-1.46-3.27-3.4h1.96c.1 1.05.82 1.87 2.65 1.87 1.96 0 2.4-.98 2.4-1.59 0-.83-.44-1.61-2.67-2.14-2.48-.6-4.18-1.62-4.18-3.67 0-1.72 1.39-2.84 3.11-3.21V4h2.67v1.95c1.86.45 2.79 1.86 2.85 3.39H14.3c-.05-1.11-.64-1.87-2.22-1.87-1.5 0-2.4.68-2.4 1.64 0 .84.65 1.39 2.67 1.91s4.18 1.39 4.18 3.91c-.01 1.83-1.38 2.83-3.12 3.16z" />
          </svg>
        </div>
      )
    },
    {
      title: 'Beneficio Total',
      value: `${yearTotal.profit.toLocaleString()}€`,
      description: 'Beneficio neto después de descontar todos los gastos de limpieza y gestión.',
      icon: (
        <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
          <svg className="w-6 h-6 text-green-600" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1.41 16.09V20h-2.67v-1.93c-1.71-.36-3.16-1.46-3.27-3.4h1.96c.1 1.05.82 1.87 2.65 1.87 1.96 0 2.4-.98 2.4-1.59 0-.83-.44-1.61-2.67-2.14-2.48-.6-4.18-1.62-4.18-3.67 0-1.72 1.39-2.84 3.11-3.21V4h2.67v1.95c1.86.45 2.79 1.86 2.85 3.39H14.3c-.05-1.11-.64-1.87-2.22-1.87-1.5 0-2.4.68-2.4 1.64 0 .84.65 1.39 2.67 1.91s4.18 1.39 4.18 3.91c-.01 1.83-1.38 2.83-3.12 3.16z" />
          </svg>
        </div>
      )
    },
    {
      title: 'Ocupación Media',
      value: `${(yearTotal.occupancyRate / monthCount).toFixed(1)}%`,
      description: 'Porcentaje promedio de días ocupados durante el año.',
      icon: (
        <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
          <svg className="w-6 h-6 text-blue-600" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 3h-4.18C14.4 1.84 13.3 1 12 1c-1.3 0-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm2 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z" />
          </svg>
        </div>
      )
    },
    {
      title: 'Total Reservas',
      value: yearTotal.reservations.toString(),
      description: 'Número total de reservas realizadas durante el año.',
      icon: (
        <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
          <svg className="w-6 h-6 text-purple-600" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11z" />
          </svg>
        </div>
      )
    }
  ];

  const monthlyData = MONTHS.map((month, index) => {
    const monthStat = yearlyStats.find(stat => stat.month === index + 1) || {
      revenue: 0,
      profit: 0,
      cleaningCosts: 0,
      inmohostCosts: 0,
      occupancyRate: 0,
      reservations: 0
    };
    return {
      name: month,
      ingresos: monthStat.revenue,
      beneficio: monthStat.profit,
      gastosLimpieza: monthStat.cleaningCosts,
      gastosInmohost: monthStat.inmohostCosts,
      ocupacion: monthStat.occupancyRate,
      reservas: monthStat.reservations
    };
  });

  const distributionData = [
    { name: 'Beneficio', value: yearTotal.profit },
    { name: 'Gastos Limpieza', value: yearTotal.cleaningCosts },
    { name: 'Gastos INMOHOST', value: yearTotal.inmohostCosts }
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Resumen Anual</h2>
          <p className="text-gray-500">
            Datos acumulados del año {selectedYear}
            {selectedYear === new Date().getFullYear() && ' (año en curso)'}
          </p>
        </div>
        <Select
          value={selectedYear.toString()}
          onChange={(e) => setSelectedYear(Number(e.target.value))}
          options={availableYears.map(year => ({
            value: year.toString(),
            label: year.toString()
          }))}
          className="w-32"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {summaryCards.map((card, index) => (
          <Card key={index} className="p-6">
            <div className="flex items-center space-x-4">
              {card.icon}
              <div>
                <p className="text-sm text-gray-500">{card.title}</p>
                <p className="text-2xl font-bold">{card.value}</p>
                <p className="text-xs text-gray-500 mt-1">{card.description}</p>
              </div>
            </div>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">Evolución de Ingresos y Beneficios</h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="ingresos" name="Ingresos" fill="#ef4444" />
                <Bar dataKey="beneficio" name="Beneficio" fill="#22c55e" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">Distribución de Ingresos</h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={distributionData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {distributionData.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>
    </div>
  );
};